import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Button, Container, ButtonGroup, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { warehouseAPI } from '../api/warehouseAPI';
import ResizableTable from '../components/ResizableTable'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import PaginationComponent from '../components/Pagination'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function WarehousesListScreen() {
    const { hasPermission } = usePermissions()
    const [warehouses, setWarehouses] = useState([])
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('warehouseColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            name: 'auto',
            locations: 100,
            address: 'auto',
            actions: 100
        };
    });

    useEffect(() => {
        localStorage.setItem('warehouseColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchWarehouses() {
            try {
                setLoading(true);
                const response = await warehouseAPI.getAll(currentPage, pageSize, searchTerm, showDeactivated);
                const data = response.results;
                setWarehouses(data);
                setTotalPages(response.count);
            } catch (error) {
                console.error('Error fetching warehouses:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchWarehouses()
    }, [currentPage, searchTerm, pageSize, showDeactivated])

    const handleRowClick = (warehouseId) => {
        navigate(`/warehouse/${warehouseId}`)
    }

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleToggleActive = async (e, warehouse) => {
        e.stopPropagation();
        
        if (warehouse.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this warehouse?')) {
                return;
            }
        }

        try {
            const updatedWarehouse = {
                ...warehouse,
                is_active: !warehouse.is_active
            };
            
            await warehouseAPI.update(warehouse._id, updatedWarehouse);
            
            setWarehouses(warehouses.map(w => 
                w._id === warehouse._id 
                    ? updatedWarehouse
                    : w
            ));
        } catch (error) {
            console.error('Error toggling warehouse status:', error);
            alert(`Failed to update warehouse: ${error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Warehouses</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search warehouses..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-secondary"
                        onClick={() => {
                            setShowDeactivated(!showDeactivated);
                            setCurrentPage(1);
                        }}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    {hasPermission('locations.modify') && (
                        <Button
                            onClick={() => navigate('/warehouse/create')}
                            variant="success"
                        >
                            Create Warehouse
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        columns={[
                            {
                                id: 'name',
                                header: 'Name',
                                cell: (row) => (
                                    <>
                                        {highlightText(row.name, searchTerm)}
                                        {!row.is_active && (
                                            <span className="ms-2 text-muted fst-italic">
                                                (Inactive)
                                            </span>
                                        )}
                                    </>
                                )
                            },
                            {
                                id: 'locations',
                                header: 'Locations',
                                cell: (row) => (
                                    <>
                                        {row.is_active && (
                                            <Button
                                                variant="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/warehouse/${row._id}/locations`,
                                                        {
                                                            state: {
                                                                warehouseName: row.name
                                                            }
                                                        }
                                                    );
                                                }}
                                                size="sm"
                                            >
                                                Locations
                                            </Button>
                                        )}
                                    </>
                                )
                            },
                            {
                                id: 'address',
                                header: 'Address',
                                cell: (row) => highlightText(row.address, searchTerm)
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    hasPermission('locations.modify') && (
                                        <ButtonGroup size="sm" className="gap-1">
                                            <Button
                                                variant={row.is_active ? "danger" : "success"}
                                                size="sm"
                                                onClick={(e) => handleToggleActive(e, row)}
                                            >
                                                {row.is_active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        </ButtonGroup>
                                    )
                                )
                            }
                        ]}
                        data={warehouses}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => handleRowClick(row._id)}
                        tableId="warehouses_table"
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default WarehousesListScreen
